import {
  AudioOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { Button, Empty, Typography } from 'antd';
import { Artifact } from 'components/Artifacts/types';
import { DragEvent, useState } from 'react';

const { Text } = Typography;

type DropPosition = 'before' | 'after';

type ArtifactCarouselProps = {
  items: Artifact[];
  isReordering?: boolean;
  selectedArtifactId: string | null;
  onReorder?: (draggedArtifactId: string, targetArtifactId: string, position: DropPosition) => void;
  onSelect: (artifactId: string) => void;
  onPrevious: () => void;
  onNext: () => void;
};

const getTranscriptIndicator = (artifact: Artifact) => {
  switch (artifact.transcript.status) {
    case 'COMPLETED':
      return { icon: <CheckCircleOutlined />, label: 'Transcript ready' };
    case 'FAILED':
      return { icon: <WarningOutlined />, label: 'Transcript failed' };
    case 'PROCESSING':
    case 'QUEUED':
      return { icon: <LoadingOutlined />, label: 'Transcript processing' };
    default:
      return { icon: <FileTextOutlined />, label: 'No transcript yet' };
  }
};

const getDropPosition = (event: DragEvent<HTMLElement>): DropPosition => {
  const bounds = event.currentTarget.getBoundingClientRect();
  const midpoint = bounds.left + (bounds.width / 2);
  return event.clientX <= midpoint ? 'before' : 'after';
};

export function ArtifactCarousel({
  items,
  isReordering = false,
  selectedArtifactId,
  onReorder,
  onSelect,
  onPrevious,
  onNext
}: ArtifactCarouselProps) {
  const [draggedArtifactId, setDraggedArtifactId] = useState<string | null>(null);
  const [dropTargetArtifactId, setDropTargetArtifactId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<DropPosition>('before');
  const initialSelectedIndex = items.findIndex((item) => item.id === selectedArtifactId);
  const selectedArtifact = initialSelectedIndex >= 0 ? items[initialSelectedIndex] : items[0] || null;
  const selectedIndex = selectedArtifact ? items.findIndex((item) => item.id === selectedArtifact.id) : -1;

  const clearDragState = () => {
    setDraggedArtifactId(null);
    setDropTargetArtifactId(null);
    setDropPosition('before');
  };

  const handleDragStart = (event: DragEvent<HTMLButtonElement>, artifactId: string) => {
    if (isReordering || items.length < 2) {
      event.preventDefault();
      return;
    }

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', artifactId);
    setDraggedArtifactId(artifactId);
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>, artifactId: string) => {
    if (!draggedArtifactId || draggedArtifactId === artifactId) {
      return;
    }

    event.preventDefault();
    setDropTargetArtifactId(artifactId);
    setDropPosition(getDropPosition(event));
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>, artifactId: string) => {
    event.preventDefault();

    const droppedArtifactId = draggedArtifactId || event.dataTransfer.getData('text/plain');

    if (!droppedArtifactId || droppedArtifactId === artifactId) {
      clearDragState();
      return;
    }

    const nextDropPosition = getDropPosition(event);
    onReorder?.(droppedArtifactId, artifactId, nextDropPosition);
    clearDragState();
  };

  if (!selectedArtifact) {
    return (
      <div className="artifacts-page__panel">
        <Empty description="Create your first artifact to start browsing your collection." />
      </div>
    );
  }

  return (
    <div className="artifact-carousel">
      <div className="artifact-carousel__header">
        <Text strong>Collection</Text>
        <Text className="artifact-carousel__count">
          {selectedIndex + 1} of {items.length}
        </Text>
      </div>

      <div className="artifact-carousel__rail">
        <Button
          className="artifact-carousel__nav"
          icon={<LeftOutlined />}
          onClick={onPrevious}
          disabled={selectedIndex <= 0}
          aria-label="Previous artifact"
        />

        <div className="artifact-carousel__items">
          {items.map((artifact) => {
            const isSelected = artifact.id === selectedArtifact.id;
            const isDragged = artifact.id === draggedArtifactId;
            const isDropTarget = artifact.id === dropTargetArtifactId && artifact.id !== draggedArtifactId;
            const transcriptIndicator = getTranscriptIndicator(artifact);
            const subtitle = artifact.memoryDateLabel || artifact.memoryDate || 'No date added';

            return (
              <button
                key={artifact.id}
                type="button"
                aria-pressed={isSelected}
                aria-label={`Select artifact ${artifact.title || 'Untitled artifact'}`}
                className={[
                  'artifact-carousel__item',
                  isSelected ? 'is-selected' : '',
                  isDragged ? 'is-dragging' : '',
                  isDropTarget && dropPosition === 'before' ? 'is-drop-before' : '',
                  isDropTarget && dropPosition === 'after' ? 'is-drop-after' : ''
                ].filter(Boolean).join(' ')}
                onClick={() => onSelect(artifact.id)}
                draggable={!isReordering && items.length > 1}
                onDragStart={(event) => handleDragStart(event, artifact.id)}
                onDragOver={(event) => handleDragOver(event, artifact.id)}
                onDragLeave={() => {
                  if (dropTargetArtifactId === artifact.id) {
                    setDropTargetArtifactId(null);
                  }
                }}
                onDragEnd={clearDragState}
                onDrop={(event) => handleDrop(event, artifact.id)}
              >
                <div className="artifact-carousel__thumb">
                  {artifact.image?.thumbnailUrl || artifact.image?.url ? (
                    <img src={artifact.image?.thumbnailUrl || artifact.image?.url || ''} alt={artifact.title || 'Artifact thumbnail'} />
                  ) : (
                    <div className="artifact-carousel__thumb-fallback">No image</div>
                  )}
                </div>

                <div className="artifact-carousel__body">
                  <Text className="artifact-carousel__title" ellipsis>
                    {artifact.title || 'Untitled artifact'}
                  </Text>
                  <Text className="artifact-carousel__subtitle" ellipsis>
                    {subtitle}
                  </Text>

                  <div className="artifact-carousel__meta">
                    {artifact.audio && (
                      <span className="artifact-carousel__meta-pill">
                        <AudioOutlined /> Audio
                      </span>
                    )}
                    <span className="artifact-carousel__meta-pill">
                      {transcriptIndicator.icon} {transcriptIndicator.label}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <Button
          className="artifact-carousel__nav"
          icon={<RightOutlined />}
          onClick={onNext}
          disabled={selectedIndex >= items.length - 1}
          aria-label="Next artifact"
        />
      </div>
    </div>
  );
}

export default ArtifactCarousel;